import { InterfaceModelDecorator } from "./PreviewModelDecorator";
import { IModel } from "makerjs";
import { makerjs } from "../../../../source/js/shared/rendering/makerjs";
import EdgeDrillHoles from "@/Models/EdgeDrillHoles";
import GridDrillHoles from "@/Models/GridDrillHoles";
import RadialDrillHoles from "@/Models/RadialDrillHoles";
import PresetDrillHoles from "@/Models/PresetDrillHoles";
import DrillHole from "@/Models/DrillHole";
import { chainModel } from "@/functions/makerjs";
import OffsetLine from "@/Models/Lines/OffsetLine";

export class GeneratedDrillHoleDecorator implements InterfaceModelDecorator {
	constructor(
		private parentDecorator: InterfaceModelDecorator | null,
		private drillHoleInstance: EdgeDrillHoles | GridDrillHoles | RadialDrillHoles | PresetDrillHoles | null
	) {}

	getModel(): IModel {
		let model = { models: {} } as IModel;

		if (this.parentDecorator) {
			model = this.parentDecorator.getModel();
		}

		if (this.drillHoleInstance === null) {
			return model;
		}

		let drillHoleModel = this.drillHoleInstance.getAsModel();
		drillHoleModel = makerjs.cloneObject(drillHoleModel);

		makerjs.model.scale(drillHoleModel, 10, true); // Convert to mm
		makerjs.model.addModel(model, drillHoleModel, "drillHoleModel");

		return model;
	}
}

export class CustomDrillHoleDecorator implements InterfaceModelDecorator {
	constructor(
		private parentDecorator: InterfaceModelDecorator | null,
		private drillHoles: DrillHole[]
	) {}

	getModel(): IModel {
		let model = { models: {} } as IModel;

		if (this.parentDecorator) {
			model = this.parentDecorator.getModel();
		}

		const drillHolesModel = { models: {} } as IModel;
		const measure = makerjs.measure.modelExtents(model.models.shapeModel.models.baseModel);

		this.drillHoles.forEach( ( drillHole, index ) => {
			let drillHoleModel = drillHole.getAsModel();

			drillHoleModel = makerjs.cloneObject(drillHoleModel);
			makerjs.model.scale(drillHoleModel, 10, true); // Convert to mm
			makerjs.model.addModel(drillHolesModel, drillHoleModel, `drillHole_${index}`);
			makerjs.model.moveRelative(drillHoleModel, [measure.low[0], measure.low[1]]);
		} );

		let combinedModel = { models: {} } as IModel;

		Object.values(drillHolesModel.models).forEach((model: IModel) => {
			combinedModel = makerjs.model.combineUnion(combinedModel, model);
		});

		combinedModel = chainModel(combinedModel);
		makerjs.model.addModel(model, combinedModel, "drillHoleModel");

		return model;
	}
}

/**
 * This decorator is used to show and add the current drillHole in the preview.
 * So we can highlight this drillHole seperated from the combined drillHoles model.
 */
export class CurrentCustomDrillHoleDecorator implements InterfaceModelDecorator {
	constructor(
		private parentDecorator: InterfaceModelDecorator | null,
		private drillHole: DrillHole | null,
		private index: number | null,
		private focusedElement: string
	) {}

	getModel(): IModel {
		let model = { models: {} } as IModel;

		if (this.parentDecorator) {
			model = this.parentDecorator.getModel();
		}

		if (!this.drillHole) {
			return model;
		}

		const measure = makerjs.measure.modelExtents(model.models.shapeModel.models.baseModel);

		let drillHoleModel = this.drillHole.getAsModel();
		drillHoleModel.layer = 'active-drillhole';
		makerjs.model.scale(drillHoleModel, 10, true); // Convert to mm
		drillHoleModel = makerjs.cloneObject(drillHoleModel);

		const offsetLines = this.drillHole.getOffsetLines(this.focusedElement);

		offsetLines.forEach((offsetLine: OffsetLine, index: number) => {
			const offsetLineModel = offsetLine.getModel();
			offsetLineModel.layer = "offset-lines";
			makerjs.model.addModel(drillHoleModel, offsetLineModel, `offsetLine_${index}`);
		});

		if(this.index !== null) {
			const label = this.drillHole.getLabel(`${this.index + 1}`);
			makerjs.model.addModel(drillHoleModel, label.getModel(), `caption_${this.index + 1}`);
		}

		makerjs.model.moveRelative(drillHoleModel, [measure.low[0], measure.low[1]]);

		makerjs.model.addModel(model, drillHoleModel, "currentDrillHole");

		return model;
	}
}

export class CurrentGeneratedDrillHoleDecorator implements InterfaceModelDecorator {
	constructor(
		private parentDecorator: InterfaceModelDecorator | null,
		private drillHoleInstance: EdgeDrillHoles | GridDrillHoles | RadialDrillHoles | PresetDrillHoles | null,
		private focusedElement?: string
	) {}

	getModel(): IModel {
		let model = { models: {} } as IModel;

		if (this.parentDecorator) {
			model = this.parentDecorator.getModel();
		}

		if (this.drillHoleInstance === null) {
			return model;
		}

		let drillHoleModel = this.drillHoleInstance.getAsModel();
		drillHoleModel = makerjs.cloneObject(drillHoleModel);

		if(this.drillHoleInstance instanceof EdgeDrillHoles) {
			const currentDrillHoleModel = this.drillHoleInstance.getCurrentEdgeDrillHoles(this.focusedElement);

			if(currentDrillHoleModel) {
				drillHoleModel = currentDrillHoleModel;
				drillHoleModel.layer = 'active-drillhole';
			}

			makerjs.model.scale(drillHoleModel, 10, true); // Convert to mm
			makerjs.model.addModel(model, drillHoleModel, "drillHoleModel");
		}

		return model;
	}
}

export default class DrillHoleOffsetLineDecorator implements InterfaceModelDecorator {
	constructor(
		private parentDecorator: InterfaceModelDecorator | null,
		private drillHoleInstance: EdgeDrillHoles | GridDrillHoles | RadialDrillHoles | PresetDrillHoles | null,
		private focusedElement?: string
	) {}

	getModel(): IModel {
		let model = { models: {} } as IModel;

		if (this.parentDecorator) {
			model = this.parentDecorator.getModel();
		}

		if (this.drillHoleInstance === null) {
			return model;
		}

		const measure = makerjs.measure.modelExtents(model.models.shapeModel.models.baseModel);
		const offsetLines = this.drillHoleInstance.getOffsetLines(this.focusedElement);

		offsetLines.forEach((offsetLine: OffsetLine, index: number) => {
			const offsetLineModel = offsetLine.getModel();
			offsetLineModel.layer = "offset-lines";
			makerjs.model.moveRelative(offsetLineModel, [measure.low[0], measure.low[1]]);
			makerjs.model.addModel(model.models.shapeModel, offsetLineModel, `offsetLine_${index}`);
		});

		return model;
	}
}
